import { css } from '@emotion/react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Divider,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import React, { FC, useContext, useState } from 'react';

import { SidebarMenuItem } from '@components/Layout/Sidebar/constants';
import {
  setIsSidebarOpen,
} from '@components/Layout/Sidebar/utils';
import SidebarNavListItem from '@components/Layout/SidebarNavListItem/SidebarNavListItem';
import { staticFileLocation } from '@server/constants';
import { darkBlue, lightGrey2, white } from '@styles/colors';
import { bold } from '@styles/fontWeights';
import { authContext } from '@staffedupusa/shared-components';

const expandedDrawerWidth = 240;
const collapsedDrawerWidth = 70;

const ListSekelton = () => (
  <ListItem disablePadding>
    <Skeleton
      variant="rectangular"
      width="-webkit-fill-available"
      sx={{
        backgroundColor: 'grey',
        height: '40px',
        borderRadius: 5,
        margin: 1.5,
      }}
    />
  </ListItem>
);

type SidebarProps = {
  menuItems: SidebarMenuItem[] | null;
};

const Sidebar: FC<SidebarProps> = ({ menuItems }) => {
  const { authData } = useContext(authContext);
  const isAdmin = authData?.user?.is_admin;
  const theme = useTheme();
  const isMdOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

  const [isOpen, setIsOpen] = useState(isMdOrSmaller ? false : true);
  const [shouldOpenOnHover, setShouldOpenOnHover] = useState(
    isMdOrSmaller ? false : true,
  );

  const toggleSidebar = () => {
    setIsSidebarOpen(String(!isOpen));
    setIsOpen(!isOpen);
    setShouldOpenOnHover(isOpen);
  };

  const openSidebar = () => setIsOpen(true);

  const closeSidebar = () => setIsOpen(false);

  return (
    <Box display="inline-flex" color="white">
      <Drawer
        hideBackdrop={false}
        variant={isMdOrSmaller ? 'temporary' : 'permanent'}
        onClose={closeSidebar}
        open={isOpen}
        onMouseOver={() => shouldOpenOnHover && openSidebar()}
        // onMouseLeave={() => shouldOpenOnHover && closeSidebar()}
        PaperProps={{
          sx: {
            width: isOpen
              ? `${expandedDrawerWidth}px`
              : `${collapsedDrawerWidth}px`,
            transition: 'width 0.3s',
            color: isAdmin ? white : 'secondary.main',
            backgroundColor: isAdmin ? 'secondary.light' : white,
            overflowX: 'hidden',
            paddingX: isOpen ? 2.5 : 1.5,
            paddingBottom: 3,
          },
        }}
        sx={{
          width: isOpen
            ? `${expandedDrawerWidth}px`
            : `${collapsedDrawerWidth}px`,
          transition: 'width 0.3s',
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          paddingY={3}
          marginRight='6px'
        >
          <img
            src={
              isAdmin
                ? `${staticFileLocation}img/logos/WhiteStamp.png`
                : `${staticFileLocation}img/logos/LogoStamp.png`
            }
            alt="Logo"
            css={css`
              max-height: 45px;
              display: block;
            `}
          />
          {isOpen && (
            <Typography variant="h3" ml={isAdmin ? 3 : 1}>
              Staffed
              <Typography variant="h3" component="span" fontWeight={bold}>
                Up
              </Typography>
            </Typography>
          )}
        </Box>
        <Divider variant="middle" sx={{ borderColor: isAdmin ?  white : darkBlue }} />
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            whiteSpace: 'nowrap',
          }}
        >
          {!menuItems ? (
            <>
              <ListSekelton />
              <ListSekelton />
              <ListSekelton />
            </>
          ) : (
            menuItems &&
            menuItems.map((item) => {
              return (
                <React.Fragment key={item.text}>
                  {item.addGapBeforeItem && (
                    <ListItem
                      sx={{
                        flexGrow: 1,
                        width: 'max-content',
                      }}
                    />
                  )}

                  <SidebarNavListItem
                    item={item}
                    isSidebarOpen={isOpen}
                    setIsSidebarOpen={setIsOpen}
                  />
                </React.Fragment>
              );
            })
          )}
        </List>
      </Drawer>
      <Box sx={{ overflow: 'hidden' }}>
        <IconButton
          disableRipple
          onClick={toggleSidebar}
          color="inherit"
          sx={{
            zIndex: 1,
            color: isAdmin ? white : 'secondary.light',
            backgroundColor: isAdmin ? 'secondary.light' : white,
            borderRadius: 0,
            borderTopRightRadius: 3,
            borderBottomRightRadius: 3,
            padding: 0,
            position: 'fixed',
            ml: '-2px',
            '&:hover, &:focus': {
              backgroundColor: isAdmin ? 'secondary.light' : lightGrey2,
            },
          }}
          edge="start"
        >
          {isOpen ? <ChevronLeftIcon /> : <MoreVertIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default Sidebar;
